import React, { useState, useCallback, useRef } from 'react';
import { FiPackage, FiSearch } from 'react-icons/fi';
import { Button } from '../../ui/Button';
import { Input } from '../../ui/Input';
import { Spinner } from '../../ui/Spinner';
import { Container } from '../../layout/Container';
import { TrackingModal } from './TrackingModal';
import { CONTACT_INFO } from '../../../utils/constants';
import { useLanguage } from '../../../contexts/LanguageContext';

const getDemoData = (t) => ({
  trackingNumber: 'DEMO',
  currentStep: 2,
  estimatedDelivery: t('tracking.estimatedDelivery'),
  steps: [
    { id: 1, title: t('tracking.demoSteps.step1.title'), location: t('tracking.demoSteps.step1.location'), date: '18 Kas 14:30', icon: '📦', completed: true },
    { id: 2, title: t('tracking.demoSteps.step2.title'), location: t('tracking.demoSteps.step2.location'), date: '19 Kas 08:00', icon: '✈️', completed: true },
    { id: 3, title: t('tracking.demoSteps.step3.title'), location: t('tracking.demoSteps.step3.location'), date: '19 Kas 11:00', icon: '🛫', completed: false },
    { id: 4, title: t('tracking.demoSteps.step4.title'), location: t('tracking.demoSteps.step4.location'), date: '20 Kas 09:00', icon: '🏢', completed: false },
    { id: 5, title: t('tracking.demoSteps.step5.title'), location: t('tracking.demoSteps.step5.location'), date: '25 Kas 16:00', icon: '🏠', completed: false },
  ],
});

/**
 * Tracking section component with search and modal
 */
const TrackingSection = () => {
  const { t } = useLanguage();
  const [trackingNumber, setTrackingNumber] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const inputRef = useRef(null);

  const handleTrack = useCallback(async () => {
    setError('');

    if (!trackingNumber.trim()) {
      setError(t('tracking.error'));
      inputRef.current?.focus();
      return;
    }

    if (trackingNumber.toUpperCase() === 'DEMO') {
      setShowModal(true);
      return;
    }

    setIsLoading(true);
    try {
      await new Promise((resolve) => setTimeout(resolve, 1000));
      setError(t('tracking.notFound'));
    } finally {
      setIsLoading(false);
    }
  }, [trackingNumber, t]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter') handleTrack();
  }, [handleTrack]);

  const handleInputChange = useCallback((e) => {
    setTrackingNumber(e.target.value);
    if (error) setError('');
  }, [error]);

  const closeModal = useCallback(() => {
    setShowModal(false);
  }, []);

  return (
    <>
      <section className="relative py-20 lg:py-32 bg-gray-50 overflow-hidden" aria-labelledby="tracking-title">
        {/* Decorative Background Elements */}
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none opacity-5">
          <svg className="absolute w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
            <path d="M0 100 L100 0 L100 100 Z" fill="currentColor" className="text-primary" />
          </svg>
        </div>

        <Container className="relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <div className="bg-white/80 backdrop-blur-xl rounded-3xl shadow-soft-2xl border border-white/50 p-8 md:p-12 lg:p-16">

              {/* Header */}
              <div className="mb-10 sm:mb-12 space-y-4">
                <span className="inline-block py-1 px-3 rounded-full bg-blue-50 text-primary text-sm font-bold tracking-wider uppercase">
                  {t('tracking.title')}
                </span>
                <h2 id="tracking-title" className="text-3xl sm:text-4xl md:text-5xl font-display font-bold text-gray-900 tracking-tight">
                  {t('tracking.description')}
                </h2>
              </div>

              {/* Input Section */}
              <div className="max-w-2xl mx-auto mb-12">
                <div className="relative group">
                  <div className="absolute inset-0 bg-primary/20 rounded-2xl blur-xl transition-opacity opacity-0 group-hover:opacity-100 duration-500"></div>
                  <div className="relative flex flex-col sm:flex-row gap-3 p-2 bg-white rounded-2xl shadow-lg border border-gray-100">
                    <div className="relative flex-1">
                      <div className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">
                        <FiPackage size={24} aria-hidden="true" />
                      </div>
                      <label htmlFor="tracking-input" className="sr-only">
                        {t('tracking.trackingLabel')}
                      </label>
                      <input
                        type="text"
                        id="tracking-input"
                        className="w-full h-14 pl-12 pr-4 text-lg bg-transparent border-none focus:ring-0 text-gray-900 placeholder-gray-400"
                        placeholder={t('tracking.placeholder')}
                        value={trackingNumber}
                        onChange={handleInputChange}
                        onKeyDown={handleKeyDown}
                        ref={inputRef}
                        aria-describedby={error ? 'tracking-error' : 'tracking-hint'}
                        aria-invalid={!!error}
                        autoComplete="off"
                      />
                    </div>
                    <Button
                      type="button"
                      onClick={handleTrack}
                      disabled={isLoading}
                      size="lg"
                      className="rounded-xl shadow-lg shadow-primary/20 hover:shadow-primary/40 min-w-[140px]"
                      aria-label={isLoading ? t('tracking.searching') : t('tracking.track')}
                    >
                      {isLoading ? (
                        <>
                          <Spinner size="sm" className="mr-2" />
                          {t('tracking.searching')}
                        </>
                      ) : (
                        <>
                          {t('tracking.track')}
                          <FiSearch className="ml-2" aria-hidden="true" />
                        </>
                      )}
                    </Button>
                  </div>
                </div>

                {/* Hints & Errors */}
                <div className="mt-4 flex items-center justify-center min-h-[24px]">
                  {error ? (
                    <p id="tracking-error" className="text-red-500 font-medium animate-shake flex items-center gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-red-500" />
                      {error}
                    </p>
                  ) : (
                    <p id="tracking-hint" className="text-gray-500 text-sm" dangerouslySetInnerHTML={{ __html: t('tracking.hint') }} />
                  )}
                </div>
              </div>

              {/* Help Section */}
              <div className="pt-8 border-t border-gray-100">
                <p className="text-gray-600 font-medium mb-2">{t('tracking.helpText')}</p>
                <div className="flex flex-wrap justify-center gap-4 sm:gap-8 text-sm">
                  {CONTACT_INFO
                    .filter(info => info.type === 'link' && (info.href.startsWith('tel:') || info.href.startsWith('mailto:')))
                    .map((info, index) => (
                      <a key={index} href={info.href} className="flex items-center gap-2 text-gray-500 hover:text-primary transition-colors">
                        <span className="w-8 h-8 rounded-full bg-gray-50 flex items-center justify-center text-primary group-hover:bg-primary group-hover:text-white transition-colors">
                          <info.icon size={18} />
                        </span>
                        {info.content}
                      </a>
                    ))}
                </div>
              </div>
            </div>
          </div>
        </Container>
      </section>

      {/* Tracking Modal */}
      {showModal && <TrackingModal data={getDemoData(t)} onClose={closeModal} />}
    </>
  );
};

export default TrackingSection;
